import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { 
  Bell, BellRing, AlertTriangle, TrendingUp, TrendingDown,
  Calendar, CreditCard, PiggyBank, Target, Zap, Settings,
  ChevronRight, Check, X
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';

const SmartAlerts = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState({
    budget_threshold: 80,
    large_expense_amount: 100,
    unusual_spending_enabled: true,
    recurring_reminder_days: 3,
    savings_goal_enabled: true,
    weekly_summary_enabled: true,
    low_balance_amount: 100,
  });
  const [loading, setLoading] = useState(true);
  const [activeAlerts, setActiveAlerts] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [alertsRes, settingsRes] = await Promise.all([
        api.get('/alerts/active'),
        api.get('/alerts/settings'),
      ]);
      setActiveAlerts(alertsRes.data.alerts || []);
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
    } catch (error) {
      console.error('Error loading alerts');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      const newSettings = { ...settings, [key]: value };
      setSettings(newSettings);
      await api.put('/alerts/settings', newSettings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/dismiss`);
      setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alerta descartada');
    } catch (error) {
      toast.error('Error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: user?.currency || 'EUR'
    }).format(amount);
  };

  const alertTypes = [
    {
      id: 'budget_warning',
      title: 'Alerta de Presupuesto',
      description: 'Avisar cuando un presupuesto supere el umbral',
      icon: Target,
      color: 'text-amber-500',
      setting: 'budget_threshold',
      type: 'slider',
      min: 50,
      max: 100,
      unit: '%',
    },
    {
      id: 'large_expense',
      title: 'Gasto Grande',
      description: 'Avisar cuando registres un gasto superior a',
      icon: AlertTriangle,
      color: 'text-red-500',
      setting: 'large_expense_amount',
      type: 'input',
      unit: '€',
    },
    {
      id: 'unusual_spending',
      title: 'Gasto Inusual',
      description: 'Detectar patrones de gasto anormales con IA',
      icon: Zap,
      color: 'text-purple-500',
      setting: 'unusual_spending_enabled',
      type: 'toggle',
    },
    {
      id: 'recurring_reminder',
      title: 'Recordatorio de Pagos',
      description: 'Avisar días antes de un pago recurrente',
      icon: Calendar,
      color: 'text-blue-500',
      setting: 'recurring_reminder_days',
      type: 'input',
      unit: 'días',
    },
    {
      id: 'savings_goal',
      title: 'Progreso de Metas',
      description: 'Notificar avances en metas de ahorro',
      icon: PiggyBank,
      color: 'text-emerald-500',
      setting: 'savings_goal_enabled',
      type: 'toggle',
    },
    {
      id: 'weekly_summary',
      title: 'Resumen Semanal',
      description: 'Recibir un resumen de tus finanzas cada lunes',
      icon: TrendingUp,
      color: 'text-primary',
      setting: 'weekly_summary_enabled',
      type: 'toggle',
    },
    {
      id: 'low_balance',
      title: 'Saldo Bajo',
      description: 'Avisar cuando una cuenta baje de',
      icon: CreditCard,
      color: 'text-orange-500',
      setting: 'low_balance_amount',
      type: 'input',
      unit: '€',
    },
  ];

  const getAlertIcon = (type) => {
    const config = alertTypes.find(a => a.id === type);
    return config?.icon || Bell;
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      case 'medium': return 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800';
      default: return 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800';
    }
  };

  return (
    <div className="space-y-6 pb-8" data-testid="smart-alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BellRing className="text-primary" />
            Alertas Inteligentes
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configura notificaciones personalizadas para tus finanzas
          </p>
        </div>
      </div>

      {/* Active Alerts */}
      {activeAlerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="text-amber-500" />
              Alertas Activas ({activeAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeAlerts.map((alert) => {
              const Icon = getAlertIcon(alert.type);
              return (
                <div 
                  key={alert.id}
                  className={cn(
                    "flex items-start gap-3 p-4 rounded-lg border",
                    getAlertColor(alert.severity)
                  )}
                >
                  <Icon className={cn(
                    "w-5 h-5 mt-0.5",
                    alert.severity === 'high' ? 'text-red-500' : 
                    alert.severity === 'medium' ? 'text-amber-500' : 'text-blue-500'
                  )} />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{alert.title}</p>
                    <p className="text-sm text-muted-foreground">{alert.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(alert.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => dismissAlert(alert.id)}
                    className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <X size={16} />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Alert Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="text-muted-foreground" />
            Configuración de Alertas
          </CardTitle>
          <CardDescription>
            Personaliza cuándo y cómo quieres recibir notificaciones
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {alertTypes.map((alertType) => {
            const Icon = alertType.icon;
            const value = settings[alertType.setting];
            
            return (
              <div 
                key={alertType.id}
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center bg-muted"
                )}>
                  <Icon className={cn("w-5 h-5", alertType.color)} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{alertType.title}</h4>
                  <p className="text-sm text-muted-foreground">{alertType.description}</p>
                  
                  {alertType.type === 'toggle' && (
                    <div className="mt-2">
                      <Switch 
                        checked={value}
                        onCheckedChange={(checked) => updateSetting(alertType.setting, checked)}
                      />
                    </div>
                  )}
                  
                  {alertType.type === 'slider' && (
                    <div className="mt-3 flex items-center gap-4">
                      <Slider
                        value={[value]}
                        onValueChange={([v]) => updateSetting(alertType.setting, v)}
                        min={alertType.min}
                        max={alertType.max}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-sm font-medium w-16 text-right">
                        {value}{alertType.unit}
                      </span>
                    </div>
                  )}
                  
                  {alertType.type === 'input' && (
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="number"
                        value={value}
                        onChange={(e) => updateSetting(alertType.setting, parseFloat(e.target.value))}
                        className="w-20 sm:w-24"
                      />
                      <span className="text-sm text-muted-foreground">{alertType.unit}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Alertas con IA</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Nuestro sistema analiza tus patrones de gasto y te avisa cuando detecta 
                comportamientos inusuales, como gastos más altos de lo normal en una categoría 
                o transacciones sospechosas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartAlerts;
